import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SecondarySource, SubscriptionSource } from '../../../models';
import DateTime from '../../../utils/DateTime';
import { ToggleRenewal } from './TurnOffAutoRenew/ToggleRenewal';
import { fromSubscription } from '../../../store';

const RenewalStatus = (
  {
    productName,
    source,
    secondarySource,
    autoRenewalEnabled,
    canceledAt,
    expiryDate,
    recurlySubscriptionId,
  }
  : {
    productName: string,
    source: SubscriptionSource,
    secondarySource: SecondarySource,
    autoRenewalEnabled?: boolean,
    canceledAt?: Date,
    expiryDate: Date,
    recurlySubscriptionId?: number,
  },
) => {
  const intl = useIntl();
  const trueUpgradeInfo = useSelector(fromSubscription.selectTrueUpgradeInfo);
  const [autoRenew, setAutoRenew] = React.useState(
    (autoRenewalEnabled ?? false) && !canceledAt,
  );

  useEffect(() => {
    if (trueUpgradeInfo?.reserved) {
      setAutoRenew(false);
    }
  }, [trueUpgradeInfo]);

  const renderStatus = () => (
    <Box
      display="flex"
      flexDirection="row"
      alignContent="center"
      alignItems="center"
      paddingBottom={1}
    >
      <Typography
        variant="body1"
        fontWeight={700}
        fontSize={14}
        paddingRight={1}
      >
        <FormattedMessage id="page.subscription.current.subscription.status.label" />
      </Typography>
      <Typography
        variant="body1"
        fontWeight={400}
        fontSize={14}
      >
        <FormattedMessage
          id={autoRenew
            ? `page.subscription.table.renewal.status.${source.toLowerCase()}.${secondarySource.toLowerCase()}`
            : 'page.subscription.table.renewal.status.expire.on'}
          values={{
            expiryDate: DateTime.formatDate(intl, expiryDate),
          }}
        />
      </Typography>
    </Box>
  );

  const renderAutoRenewStatus = () => (
    <Box
      display="flex"
      flexDirection="row"
      alignContent="center"
      alignItems="center"
      paddingLeft={1}
      color={theme => (autoRenew ? theme.refV2.sem.green[100] : theme.refV2.sem.red[100])}
    >
      <Typography variant="body2" fontWeight={400} paddingLeft={1}>
        <FormattedMessage
          id={autoRenew
            ? 'page.subscription.table.renewal.status.auto.renew.on'
            : 'page.subscription.table.renewal.status.auto.renew.off'}
        />
      </Typography>
    </Box>
  );

  return (
    <>
      {renderStatus()}
      {(source === SubscriptionSource.RECURLY && (
        <Box
          display="flex"
          flexDirection="row"
          alignContent="center"
          alignItems="center"
        >
          <ToggleRenewal
            productName={productName}
            autoRenewal={autoRenew}
            recurlySubscriptionId={recurlySubscriptionId!!}
            setAutoRenew={setAutoRenew}
            disabled={trueUpgradeInfo?.reserved ?? false}
          />
          {renderAutoRenewStatus()}
        </Box>
      ))}
    </>
  );
};

export default RenewalStatus;
